import './program-generated.scss';

import { DragDropContext, Draggable, Droppable, DropResult, DragStart } from '@hello-pangea/dnd';
import type { MenuProps } from 'antd';
import { Dropdown, Modal, notification } from 'antd';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useMockGenerateWorkoutProgramCoachProd } from 'src/api/mutations/useMockGenerateWorkoutProgramCoachProd';
import { useGenerateWorkoutProgramCoachProd } from 'src/api/mutations/useGenerateWorkoutProgramCoachProd';
import { useGetTrainerClientById } from 'src/api/mutations/useGetTrainerClients';
import { useSaveClientProgram } from 'src/api/mutations/useSaveClientProgram';
import { useSendProgramPlanToClient } from 'src/api/mutations/useSendProgramPlanToClient';
import { useGetAllMovements } from 'src/api/queries/useGetAllMovements';
import { useGetTrainerProgramById } from 'src/api/queries/useGetTrainerProgramById';
import { ReactComponent as DurationIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/duration.svg';
import { ReactComponent as EnergyLevelIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/energy-level.svg';
import { ReactComponent as FocusIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/focus.svg';
import { ReactComponent as GoalApproachIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/workouts/goal-approached.svg';
import { ReactComponent as DashboardModalDeleteIcon } from 'src/assets/images/trainer/clients/dashboard-modal-delete.svg';
import { ReactComponent as SimpleLineCheckIcon } from 'src/assets/images/onboarding-trainer/dashboard/program-generator-ai/components/simple-line-check.svg';
import { useProgramGeneratorProgressStore } from 'src/store/programGeneratorProgressStore';
import { sessionStore } from 'src/store/sessionStore';
import { auth } from 'src/config/firebase';
import MiniPTGIF from 'url:../../../../../../assets/videos/mini-pt-care.mp4';

import { Button } from '../../../../components/Button/Button';
import { confirmationSVG } from '../ProgramGeneratorAI/assets/Confirmation';
import { programGeneratorSVG } from '../ProgramGeneratorAI/assets/ProgramGenerator';
import { ProgramStepId } from '../ProgramGeneratorAI/types';
import { buildPayloadFromProgress } from '../ProgramGeneratorAI/utils/buildPayload';
import { ShareProgramModal } from '../Programs/components/ShareProgramModal/ShareProgramModal';
import { AssignToClientModal } from './components/AssignToClientModal/AssignToClientModal';
import { exerciseImagesMap } from './ExerciseImages';
import { resolveBodyProfile, isFemaleProfile } from 'src/utils/genderUtils';
import { ExerciseLibrary } from './ExerciseLibrary';
import { transformProgramStateToPlan } from './transformProgramStateToPlan';
import { GenerateProgramResponse } from './types';
import {
  ExerciseData,
  ProgramData,
  SectionData,
  transformApiToProgramData as realTransformApiToProgramData,
  transformFinalPlanToProgramData as realTransformFinalPlanToProgramData,
} from './useProgramDataTransformer';
import {
  transformApiToProgramData as mockTransformApiToProgramData,
  transformFinalPlanToProgramData as mockTransformFinalPlanToProgramData,
} from './useMockProgramDataTransformer';

const USE_MOCK = false;

const transformApiToProgramData = USE_MOCK
  ? mockTransformApiToProgramData
  : realTransformApiToProgramData;
const transformFinalPlanToProgramData = USE_MOCK
  ? mockTransformFinalPlanToProgramData
  : realTransformFinalPlanToProgramData;

const DEFAULT_OFFSETS_BY_COUNT: Record<number, number[]> = {
  1: [0], // Monday
  2: [0, 3], // Monday, Thursday
  3: [0, 2, 4], // Monday, Wednesday, Friday
  4: [0, 2, 4, 5], // Monday, Wednesday, Friday, Saturday
  5: [0, 1, 2, 3, 4], // Monday-Friday
  6: [0, 1, 2, 3, 4, 5], // Monday-Saturday
  7: [0, 1, 2, 3, 4, 5, 6], // Monday-Sunday
};

const DAY_LABELS: Record<string, { full: string; short: string }> = {
  monday: { full: 'Monday', short: 'Mon' },
  tuesday: { full: 'Tuesday', short: 'Tue' },
  wednesday: { full: 'Wednesday', short: 'Wed' },
  thursday: { full: 'Thursday', short: 'Thu' },
  friday: { full: 'Friday', short: 'Fri' },
  saturday: { full: 'Saturday', short: 'Sat' },
  sunday: { full: 'Sunday', short: 'Sun' },
  mon: { full: 'Monday', short: 'Mon' },
  tue: { full: 'Tuesday', short: 'Tue' },
  wed: { full: 'Wednesday', short: 'Wed' },
  thu: { full: 'Thursday', short: 'Thu' },
  fri: { full: 'Friday', short: 'Fri' },
  sat: { full: 'Saturday', short: 'Sat' },
  sun: { full: 'Sunday', short: 'Sun' },
};

const ORDERED_DAYS: { full: string; short: string }[] = [
  { full: 'Monday', short: 'Mon' },
  { full: 'Tuesday', short: 'Tue' },
  { full: 'Wednesday', short: 'Wed' },
  { full: 'Thursday', short: 'Thu' },
  { full: 'Friday', short: 'Fri' },
  { full: 'Saturday', short: 'Sat' },
  { full: 'Sunday', short: 'Sun' },
];

const getExerciseDisplayInfo = (ex: ExerciseData, gender: string, allMovements: any[]) => {
  const normalize = (str: string) => (str || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  const masterExercise = allMovements.find(
    (m: any) =>
      normalize(m.en_display_name) === normalize(ex.name) || normalize(m.id) === normalize(ex.name),
  );

  const isFemale = isFemaleProfile(gender);
  const currentImage = isFemale
    ? ex.image_female_url || ex.image_male_url
    : ex.image_male_url || ex.image_female_url;

  const masterImage = isFemale
    ? masterExercise?.image_female_url || masterExercise?.image_male_url
    : masterExercise?.image_male_url || masterExercise?.image_female_url;

  const displayName = masterExercise?.en_display_name || ex.name;
  const fallbackImage = exerciseImagesMap[displayName]?.[resolveBodyProfile(gender)];
  const displayImage = currentImage || masterImage || fallbackImage;

  return { displayName, displayImage };
};

const WorkoutSection: FC<{
  id: string;
  className?: string;
  children: React.ReactNode;
}> = ({ id, className = '', children }) => {
  return (
    <div id={id} className={className}>
      {children}
    </div>
  );
};

const DroppableExercisesList: FC<{
  id: string;
  children: React.ReactNode;
}> = ({ id, children }) => {
  return (
    <Droppable droppableId={id} direction="vertical">
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={`exercises-list ${snapshot.isDraggingOver ? 'match-over' : ''}`}
          style={{ minHeight: '50px' }}
        >
          {children}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
};

const DroppableSuperset: FC<{
  id: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}> = ({ id, children, style, className = '' }) => {
  return (
    <div className={`superset-content-wrapper ${className}`} style={style}>
      <div className="superset-content-inner">
        <Droppable droppableId={id} direction="vertical">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`superset-content ${snapshot.isDraggingOver ? 'match-over' : ''}`}
            >
              {children}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </div>
  );
};

const SortableExerciseCard: FC<{
  ex: ExerciseData;
  index: number;
  sectionId: string;
  gender: string;
  getExerciseMenu: (ex: ExerciseData, sectionId: string) => MenuProps['items'];
  isGenerating?: boolean;
  supersetLabel?: string;
  onDoubleClickMetrics?: (field?: 'sets' | 'reps' | 'rest') => void;
  isEditing?: boolean;
  focusField?: 'sets' | 'reps' | 'rest';
  editForm?: any;
  setEditForm?: (form: any) => void;
  onEditCancel?: () => void;
}> = ({
  ex,
  index,
  sectionId,
  gender,
  getExerciseMenu,
  isGenerating,
  supersetLabel,
  onDoubleClickMetrics,
  isEditing,
  focusField = 'reps',
  editForm,
  setEditForm,
  onEditCancel,
}) => {
  const { data: movements = [] } = useGetAllMovements();
  const { displayName, displayImage } = getExerciseDisplayInfo(ex, gender, movements);

  const setsInputRef = useRef<HTMLInputElement>(null);
  const repsInputRef = useRef<HTMLInputElement>(null);
  const restInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      const timer = setTimeout(() => {
        if (focusField === 'sets') {
          setsInputRef.current?.focus();
          setsInputRef.current?.select();
        } else if (focusField === 'rest') {
          restInputRef.current?.focus();
          restInputRef.current?.select();
        } else {
          repsInputRef.current?.focus();
          repsInputRef.current?.select();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isEditing, focusField]);

  return (
    <Draggable draggableId={ex.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={provided.draggableProps.style}
          className={`exercise-card ${snapshot.isDragging ? 'dragging' : ''}`}
        >
          {!supersetLabel && (
            <div className="drag-handle" {...provided.dragHandleProps}>
              {programGeneratorSVG.sidebar.exercisePickerDots}
            </div>
          )}

          {isGenerating ? (
            <div className="exercise-replacement-state">
              <span className="replacement-icon">
                {programGeneratorSVG.header.regenerateWorkout}
              </span>
              <span className="replacement-text">
                Hang on while I&apos;m generating a replacement...
              </span>
            </div>
          ) : (
            <>
              {supersetLabel && (
                <div
                  className={`superset-label ${supersetLabel.startsWith('B') ? 'secondary' : ''}`}
                >
                  {supersetLabel}
                </div>
              )}
              {supersetLabel && (
                <div className="drag-handle" {...provided.dragHandleProps}>
                  {programGeneratorSVG.sidebar.exercisePickerDots}
                </div>
              )}
              <div className="exercise-thumb">
                {displayImage ? (
                  <img src={displayImage} alt={displayName} />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#2a2d3d',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      color: '#8a8d9d',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                  >
                    {(displayName || 'U').slice(0, 2).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="exercise-details">
                <h5>{displayName}</h5>
                {isEditing ? (
                  <>
                    <div className="pg-inline-edit-overlay" onClick={onEditCancel} />
                    <div className="exercise-metrics metrics-grid pg-inline-edit-metrics">
                      <div className="metric-box inline-box">
                        <input
                          ref={setsInputRef}
                          type="number"
                          min="1"
                          value={editForm?.sets || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val !== '') {
                              const num = parseInt(val, 10);
                              if (isNaN(num) || num <= 0) {
                                toast.error('Sets must be greater than 0');
                                return;
                              }
                              if (num > 99) {
                                toast.error('Sets cannot exceed 99');
                                return;
                              }
                              setEditForm?.({ ...editForm, sets: num });
                            } else {
                              setEditForm?.({ ...editForm, sets: '' as any });
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              (e.target as HTMLInputElement).blur();
                              onEditCancel?.();
                            }
                          }}
                          className="pg-inline-input"
                        />
                      </div>
                      <span className="times">&times;</span>
                      <div className="metric-box inline-box with-select">
                        <input
                          ref={repsInputRef}
                          type="number"
                          min="1"
                          value={
                            editForm?.metricType === 'duration'
                              ? editForm?.duration
                              : editForm?.reps
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val !== '') {
                              const num = parseInt(val, 10);
                              const isDuration = editForm?.metricType === 'duration';
                              const fieldName = isDuration ? 'Duration' : 'Reps';
                              if (isNaN(num) || num <= 0) {
                                toast.error(`${fieldName} must be greater than 0`);
                                return;
                              }
                              const maxVal = isDuration ? 3600 : 999;
                              if (num > maxVal) {
                                toast.error(
                                  `${fieldName} cannot exceed ${maxVal}${isDuration ? ' sec' : ''}`,
                                );
                                return;
                              }
                              if (isDuration) setEditForm?.({ ...editForm, duration: num });
                              else setEditForm?.({ ...editForm, reps: num });
                            } else {
                              if (editForm?.metricType === 'duration')
                                setEditForm?.({ ...editForm, duration: '' as any });
                              else setEditForm?.({ ...editForm, reps: '' as any });
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              (e.target as HTMLInputElement).blur();
                              onEditCancel?.();
                            }
                          }}
                          className="pg-inline-input"
                        />
                        <Dropdown
                          menu={{
                            items: [
                              {
                                key: 'reps',
                                label: 'reps',
                                onClick: () => setEditForm?.({ ...editForm, metricType: 'reps' }),
                                className:
                                  editForm?.metricType === 'reps' ? 'active-week-item' : '',
                              },
                              {
                                key: 'duration',
                                label: 'sec',
                                onClick: () =>
                                  setEditForm?.({ ...editForm, metricType: 'duration' }),
                                className:
                                  editForm?.metricType === 'duration' ? 'active-week-item' : '',
                              },
                            ],
                          }}
                          trigger={['click']}
                          classNames={{
                            root: 'week-dropdown-menu-overlay pg-inline-edit-dropdown-menu-overlay',
                          }}
                          placement="bottom"
                        >
                          <button
                            className="pg-inline-select"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: 0,
                            }}
                          >
                            {editForm?.metricType === 'duration' ? 'sec' : 'reps'}
                            <span
                              className="dropdown-arrow"
                              style={{ display: 'flex', transform: 'scale(0.8)' }}
                            >
                              {programGeneratorSVG.weekProgram.weekDropdownArrow}
                            </span>
                          </button>
                        </Dropdown>
                      </div>
                      <div className="metric-box rest inline-box with-text">
                        <input
                          ref={restInputRef}
                          type="text"
                          value={(() => {
                            const m = String(
                              editForm?.restAfterExercise || editForm?.restBetweenSets || '',
                            ).match(/\d+/);
                            return m && m[0] !== '0' ? m[0] : '';
                          })()}
                          onChange={(e) => {
                            const val = e.target.value.trim();
                            if (val !== '') {
                              const num = Number(val);
                              if (isNaN(num) || num <= 0) {
                                toast.error('Rest duration must be greater than 0');
                                return;
                              }
                              if (num > 3600) {
                                toast.error('Rest duration cannot exceed 3600 sec');
                                return;
                              }
                            }
                            const formattedRest = val ? `${val}s` : '0s';
                            setEditForm?.({
                              ...editForm,
                              restAfterExercise: formattedRest,
                              restBetweenSets: formattedRest,
                            });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              (e.target as HTMLInputElement).blur();
                              onEditCancel?.();
                            }
                          }}
                          className="pg-inline-input"
                        />
                        <span>rest</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div
                    className="exercise-metrics metrics-grid"
                    onDoubleClick={() => onDoubleClickMetrics?.('reps')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div
                      className="metric-box"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        onDoubleClickMetrics?.('sets');
                      }}
                    >
                      {ex.sets || 1}
                    </div>{' '}
                    <span className="times">&times;</span>
                    <div
                      className="metric-box"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        onDoubleClickMetrics?.('reps');
                      }}
                    >
                      {ex.metricType === 'duration'
                        ? `${ex.duration || 60} sec`
                        : `${ex.reps || 30} reps`}
                    </div>
                    <div
                      className="metric-box rest"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        onDoubleClickMetrics?.('rest');
                      }}
                    >
                      {(() => {
                        const m = String(ex.restAfterExercise || ex.restBetweenSets || '').match(
                          /\d+/,
                        );
                        const num = m ? parseInt(m[0], 10) : 0;
                        return num > 0 ? `${num}s rest` : 'no rest';
                      })()}
                    </div>
                  </div>
                )}
              </div>
              <Dropdown
                menu={{ items: getExerciseMenu(ex, sectionId) }}
                trigger={['click']}
                placement="bottomRight"
                classNames={{ root: 'action-dropdown-menu-overlay' }}
              >
                <button className="icon-btn">{programGeneratorSVG.exerciseAction.menu}</button>
              </Dropdown>
            </>
          )}
        </div>
      )}
    </Draggable>
  );
};

const SortableRestCard: FC<{
  item: ExerciseData;
  index: number;
  sectionId: string;
  getExerciseMenu: (ex: ExerciseData, sectionId: string) => MenuProps['items'];
  isEditing?: boolean;
  editForm?: any;
  setEditForm?: (form: any) => void;
  onEditCancel?: () => void;
  onDoubleClickMetrics?: () => void;
}> = ({
  item,
  index,
  sectionId,
  getExerciseMenu,
  isEditing,
  editForm,
  setEditForm,
  onEditCancel,
  onDoubleClickMetrics,
}) => {
  return (
    <Draggable draggableId={item.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={provided.draggableProps.style}
          className={`exercise-card rest-card ${snapshot.isDragging ? 'dragging' : ''}`}
        >
          <div className="drag-handle" {...provided.dragHandleProps}>
            {programGeneratorSVG.sidebar.exercisePickerDots}
          </div>
          <div className="exercise-details">
            <h5>Rest</h5>
            {isEditing ? (
              <>
                <div className="pg-inline-edit-overlay" onClick={onEditCancel} />
                <div className="exercise-metrics pg-inline-edit-metrics">
                  <div className="metric-box inline-box with-text">
                    <input
                      type="text"
                      value={
                        editForm?.restDuration ? editForm.restDuration.replace(' sec', '') : ''
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val !== '') {
                          const num = Number(val);
                          if (isNaN(num) || num <= 0) {
                            toast.error('Rest duration must be greater than 0');
                            return;
                          }
                          if (num > 3600) {
                            toast.error('Rest duration cannot exceed 3600 sec');
                            return;
                          }
                        }
                        setEditForm?.({
                          ...editForm,
                          restDuration: val ? `${val} sec` : '0 sec',
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          (e.target as HTMLInputElement).blur();
                          onEditCancel?.();
                        }
                      }}
                      className="pg-inline-input"
                      autoFocus
                    />
                    <span>sec</span>
                  </div>
                </div>
              </>
            ) : (
              <div
                className="exercise-metrics"
                onDoubleClick={onDoubleClickMetrics}
                style={{ cursor: 'pointer' }}
              >
                <div className="metric-box">{item.restDuration || '30 sec'}</div>
              </div>
            )}
          </div>
          <Dropdown
            menu={{ items: getExerciseMenu(item, sectionId) }}
            trigger={['click']}
            placement="bottomRight"
            classNames={{ root: 'action-dropdown-menu-overlay' }}
          >
            <button className="icon-btn">{programGeneratorSVG.exerciseAction.menu}</button>
          </Dropdown>
        </div>
      )}
    </Draggable>
  );
};

const getSectionDuration = (exercises: ExerciseData[]) => {
  let totalSeconds = 0;
  exercises.forEach((ex) => {
    if (ex.isRest) {
      const match = ex.restDuration?.match(/(\d+)/);
      if (match) totalSeconds += parseInt(match[1], 10);
    } else {
      const sets = ex.sets || 1;
      const setDuration = ex.metricType === 'duration' ? ex.duration || 60 : 60;
      let restBetween = 0;
      if (ex.restBetweenSets) {
        const match = ex.restBetweenSets.match(/(\d+)/);
        if (match) restBetween = parseInt(match[1], 10);
      }
      let restAfter = 0;
      if (ex.restAfterExercise) {
        const match = ex.restAfterExercise.match(/(\d+)/);
        if (match) restAfter = parseInt(match[1], 10);
      }
      totalSeconds += sets * setDuration;
      totalSeconds += Math.max(0, sets - 1) * restBetween;
      totalSeconds += restAfter;
    }
  });
  return totalSeconds > 0 ? Math.max(1, Math.round(totalSeconds / 60)) : 0;
};

export const ProgramGenerated: FC = () => {
  const { data: movementsData = [] } = useGetAllMovements();
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [isDraftSavedModalOpen, setIsDraftSavedModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState<{
    ex: ExerciseData;
    sectionId: string;
  } | null>(null);
  const [exerciseToRemove, setExerciseToRemove] = useState<{
    sectionId: string;
    exId: string;
  } | null>(null);
  const [sectionToEmpty, setSectionToEmpty] = useState<{ sectionId: string } | null>(null);
  const [collapsedSupersets, setCollapsedSupersets] = useState<Record<string, boolean>>({});

  const [editForm, setEditForm] = useState({
    sets: 1,
    metricType: 'reps' as 'reps' | 'duration',
    reps: 30,
    duration: 60,
    restBetweenSets: '0s',
    restAfterExercise: '0s',
    restDuration: '30 sec',
  });

  useEffect(() => {
    if (editingExercise) {
      const initialRest =
        editingExercise.ex.restAfterExercise || editingExercise.ex.restBetweenSets || '0s';
      setEditForm({
        sets: editingExercise.ex.sets || 1,
        metricType: editingExercise.ex.metricType || 'reps',
        reps: editingExercise.ex.reps || 30,
        duration: editingExercise.ex.duration || 60,
        restBetweenSets: initialRest,
        restAfterExercise: initialRest,
        restDuration: editingExercise.ex.restDuration || '30 sec',
      });
    }
  }, [editingExercise]);
  const [isEditSupersetOpen, setIsEditSupersetOpen] = useState(false);
  const [editingGroupLetter, setEditingGroupLetter] = useState('A');
  const [currentWeek, setCurrentWeek] = useState(1); // Default to week 1 as per example
  const [isLibraryOpen, setIsLibraryOpen] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true,
  );
  const [editFocusField, setEditFocusField] = useState<'sets' | 'reps' | 'rest'>('reps');

  const progress = useProgramGeneratorProgressStore((state) => state.progress);

  useEffect(() => {
    // Hide outer scroll container so react-beautiful-dnd correctly maps pointers
    // without nested scroll collision bugs, while keeping layout intact.
    const dashboardMain = document.querySelector('.dashboard-main');
    if (dashboardMain) {
      const originalOverflow = (dashboardMain as HTMLElement).style.overflow;
      (dashboardMain as HTMLElement).style.overflow = 'hidden';
      return () => {
        (dashboardMain as HTMLElement).style.overflow = originalOverflow;
      };
    }
  }, []);

  const { state, search } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { programId: urlProgramId, workout: urlWorkout } = useParams<{
    programId?: string;
    workout?: string;
  }>();

  const [rawApiResponse, setRawApiResponse] = useState<GenerateProgramResponse | null>(null);

  const { data: fetchedProgram, isLoading: isFetchingProgram } = useGetTrainerProgramById(
    urlProgramId,
    !state?.payload && !rawApiResponse,
  );

  const isManually =
    new URLSearchParams(search).get('isManually') === 'true' ||
    (fetchedProgram as unknown as Record<string, unknown>)?.isManually === true ||
    (fetchedProgram as unknown as Record<string, unknown>)?.generatorType === 'manual' ||
    (fetchedProgram as unknown as Record<string, unknown>)?.type === 'manual' ||
    (state as Record<string, unknown> | null)?.isManually === true ||
    (state as Record<string, unknown> | null)?.generatorType === 'manual';

  const getDraftProgressItems = useCallback(
    (stepId?: string) => {
      if (!stepId) return [];
      if (progress[stepId] && progress[stepId].length > 0) return progress[stepId];
      const fetchedProgress = (fetchedProgram as unknown as Record<string, unknown>)?.draft as
        | { progress?: Record<string, Array<{ slug?: string; value?: string }>> }
        | undefined;
      if (fetchedProgress?.progress) {
        const camelKey = stepId.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
        if (fetchedProgress.progress[camelKey] && fetchedProgress.progress[camelKey].length > 0)
          return fetchedProgress.progress[camelKey];
        if (fetchedProgress.progress[stepId] && fetchedProgress.progress[stepId].length > 0)
          return fetchedProgress.progress[stepId];
      }
      return [];
    },
    [progress, fetchedProgram],
  );

  const getDraftProgressSlug = useCallback(
    (stepId?: string) => {
      if (!stepId) return '';
      if (progress[stepId]?.[0]?.slug) return progress[stepId][0].slug;
      const fetchedProgress = (fetchedProgram as unknown as Record<string, unknown>)?.draft as
        | { progress?: Record<string, Array<{ slug?: string; value?: string }>> }
        | undefined;
      if (fetchedProgress?.progress) {
        const camelKey = stepId.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
        if (fetchedProgress.progress[camelKey]?.[0]?.slug)
          return fetchedProgress.progress[camelKey][0].slug;
        if (fetchedProgress.progress[stepId]?.[0]?.slug)
          return fetchedProgress.progress[stepId][0].slug;
      }
      return '';
    },
    [progress, fetchedProgram],
  );

  const getDraftProgressValue = useCallback(
    (stepId?: string) => {
      if (!stepId) return '';
      if (progress[stepId]?.[0]?.value) return progress[stepId][0].value;
      const fetchedProgress = (fetchedProgram as unknown as Record<string, unknown>)?.draft as
        | { progress?: Record<string, Array<{ slug?: string; value?: string }>> }
        | undefined;
      if (fetchedProgress?.progress) {
        const camelKey = stepId.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
        if (fetchedProgress.progress[camelKey]?.[0]?.value)
          return fetchedProgress.progress[camelKey][0].value;
        if (fetchedProgress.progress[stepId]?.[0]?.value)
          return fetchedProgress.progress[stepId][0].value;
      }
      return '';
    },
    [progress, fetchedProgram],
  );

  const getValue = (stepId: string) => progress[stepId]?.[0]?.value || '';
  const getArrayValue = (stepId: string) => progress[stepId] || [];

  const clientNameFromProgress = (getValue(ProgramStepId.ClientName) as string) || '';
  const gender = (getValue(ProgramStepId.ClientBodyProfile) as string) || 'Male';
  const durationRaw =
    fetchedProgram?.duration ||
    getDraftProgressValue(ProgramStepId.ProgramDuration) ||
    getValue(ProgramStepId.ProgramDuration) ||
    '12';
  const duration = parseInt(String(durationRaw).replace(/\D/g, ''), 10) || 12;
  const daysArray = getDraftProgressItems(ProgramStepId.HowOftenTrain);
  const existingDaysPerWeek =
    (fetchedProgram as unknown as Record<string, unknown>)?.datetimeList &&
    Array.isArray((fetchedProgram as unknown as Record<string, unknown>).datetimeList) &&
    duration
      ? Math.ceil(
          ((fetchedProgram as unknown as Record<string, unknown>).datetimeList as unknown[])
            .length / duration,
        )
      : 0;
  const howOftenTrain =
    daysArray.length > 0 ? daysArray.length : existingDaysPerWeek > 0 ? existingDaysPerWeek : 4;
  const howOftenTrainReminder =
    getDraftProgressValue(ProgramStepId.HowOftenTrainReminder) ||
    getDraftProgressSlug(ProgramStepId.HowOftenTrainReminder) ||
    '';

  const mapFitnessLevel = (level: string) => {
    switch (level.toLowerCase()) {
      case 'beginner':
        return 'Beginner';
      case 'early-intermediate':
        return 'Early Intermediate';
      case 'intermediate':
        return 'Intermediate';
      case 'advanced':
        return 'Advanced';
      case 'athlete':
        return 'Athlete';
      default:
        return level ? level.charAt(0).toUpperCase() + level.slice(1) : 'Beginner';
    }
  };

  const experienceLevelRaw =
    getDraftProgressValue(ProgramStepId.FitnessLevel) ||
    (getValue(ProgramStepId.FitnessLevel) as string) ||
    'Intermediate';
  const experienceLevel = mapFitnessLevel(experienceLevelRaw);

  const levels = ['Beginner', 'Early Intermediate', 'Intermediate', 'Advanced', 'Athlete'];
  const levelIndex = levels.findIndex((l) => l === experienceLevel);
  const dotCount = levelIndex !== -1 ? levelIndex + 1 : 3;
  const programGoal = (
    getDraftProgressValue(ProgramStepId.TrainingGoal) ||
    (getValue(ProgramStepId.TrainingGoal) as string) ||
    'Build Muscle'
  ).replace(/-/g, ' ');
  const painInjuriesItems =
    getDraftProgressItems(ProgramStepId.InjuriesParts).length > 0
      ? getDraftProgressItems(ProgramStepId.InjuriesParts)
      : getArrayValue(ProgramStepId.InjuriesParts);
  const painInjuriesRaw = painInjuriesItems.map((i) => i.value);
  const painInjuries = painInjuriesRaw.length > 0 ? painInjuriesRaw.join(', ') : 'None';

  const getDisplayTitle = (slug: string) => {
    return (slug || '')
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const mapEnergyLevel = (level: string) => {
    const mapping: any = {
      'i-can-go-all-day': 'High (can go all day)',
      'i-crash-by-mid-afternoon': 'Mid (crash by mid-afternoon)',
      'i-struggle-to-get-out-of-bed': 'Low (struggle to get out of bed)',
      'i-have-steady-energy': 'Steady (have steady energy)',
    };
    return mapping[level] || 'Steady';
  };

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const toTitleCase = (str: string) =>
    str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

  const handlePrevWeek = () => {
    if (currentWeek > 1) setCurrentWeek(currentWeek - 1);
  };

  const handleNextWeek = () => {
    if (currentWeek < duration) setCurrentWeek(currentWeek + 1);
  };

  const weekItems: MenuProps['items'] = [...Array(duration)].map((_, i) => {
    const weekNum = i + 1;
    return {
      key: weekNum.toString(),
      label: `Week ${weekNum}`,
      onClick: () => setCurrentWeek(weekNum),
      className: currentWeek === weekNum ? 'active-week-item' : '',
    };
  });

  // Drag and Drop State

  const [programData, setProgramData] = useState<ProgramData>({});
  const [currentWorkout, setCurrentWorkout] = useState(1);

  // Only use wizard progress store name when navigating from the AI wizard (state.payload present)
  const wizardClientName = state?.payload ? clientNameFromProgress : '';
  const { mutateAsync: mockMutateAsync } = useMockGenerateWorkoutProgramCoachProd();
  const { mutateAsync: realMutateAsync } = useGenerateWorkoutProgramCoachProd();
  const mutateAsync = USE_MOCK ? mockMutateAsync : realMutateAsync;
  const { mutateAsync: saveProgram, isPending: isSaving } = useSaveClientProgram();
  const { mutate: assignToClient, isPending: isAssigningToClient } = useSendProgramPlanToClient();
  const [hasSentToClient, setHasSentToClient] = useState(false);
  const [isGeneratingProgram, setIsGeneratingProgram] = useState(false);
  const isProgramLoaded = Boolean(state?.payload || rawApiResponse || fetchedProgram);
  const showHeaderStats =
    !isManually && isProgramLoaded && !isFetchingProgram && !isGeneratingProgram;
  const showRegenerateBtn = !isManually && isProgramLoaded && !isFetchingProgram;

  useEffect(() => {
    if (fetchedProgram?.isSentToClient) {
      setHasSentToClient(true);
    }
  }, [fetchedProgram?.isSentToClient]);

  const [savedProgramId, setSavedProgramId] = useState<string | null>(urlProgramId || null);

  const fetchPromise = useRef<Promise<any> | null>(null);

  const trainingGoalSlug = getDraftProgressSlug(ProgramStepId.TrainingGoal);
  const getApproachStepId = (slug: string) => {
    switch (slug) {
      case 'increase-strength':
        return ProgramStepId.IncreaseStrengthApproach;
      case 'build-muscle':
        return ProgramStepId.BuildMuscleApproach;
      case 'weight-loss':
        return ProgramStepId.WeightLossApproach;
      case 'endurance':
        return ProgramStepId.EnduranceApproach;
      case 'mobility':
        return ProgramStepId.MobilityApproach;
      case 'performance':
        return ProgramStepId.PerformanceApproach;
      case 'general-fitness':
        return ProgramStepId.GeneralFitnessApproach;
      case 'rehabilitation':
        return ProgramStepId.RehabilitationApproach;
      case 'injury-prevention':
        return ProgramStepId.InjuryPreventionApproach;
      default:
        return undefined;
    }
  };

  const goalApproach = getDraftProgressValue(getApproachStepId(trainingGoalSlug)) as string;
  const trainingGoal = getDraftProgressValue(ProgramStepId.TrainingGoal) as string;
  const energyLevel = getDraftProgressValue(ProgramStepId.EnergyLevel) as string;
  const workoutDuration = (getDraftProgressValue(ProgramStepId.WorkoutDuration) as string) || '';

  const pgMainRef = useRef<HTMLDivElement>(null);

  const draftProgramId = useProgramGeneratorProgressStore((s) => s.programId);
  const draftClientId = useProgramGeneratorProgressStore((s) => s.clientId);
  const isViewingDraft = urlProgramId && draftProgramId && urlProgramId === draftProgramId;
  const activeClientId =
    state?.payload?.clientId ||
    state?.clientId ||
    (isViewingDraft ? draftClientId : null) ||
    (fetchedProgram as any)?.clientId;

  // Fetch the specific client by UID from the program plan's clientId
  const { data: clientData } = useGetTrainerClientById(activeClientId || undefined);
  const fetchedClientName = (() => {
    const match = clientData?.clients?.[0];
    return match ? `${match.name} ${match.surname}` : '';
  })();

  // Priority: fetched client name (from program plan clientId) → wizard progress name → navigation state clientName → 'Not Assigned'
  const clientName = fetchedClientName || wizardClientName || state?.clientName || 'Not Assigned';

  const getWorkoutDayOfWeek = useCallback(
    (wNum: number) => {
      // 1. Check raw datetimeList from API/fetched program
      const rawDatetimeList =
        (fetchedProgram as any)?.datetimeList ||
        (fetchedProgram as any)?.result?.program?.data?.datetimeList ||
        rawApiResponse?.program?.data?.datetimeList ||
        rawApiResponse?.result?.program?.data?.datetimeList ||
        [];

      if (Array.isArray(rawDatetimeList) && rawDatetimeList.length > 0) {
        const sortedDays = [...rawDatetimeList].sort(
          (a: any, b: any) => new Date(a.ymd).getTime() - new Date(b.ymd).getTime(),
        );
        const daysPerWeek = Math.ceil(sortedDays.length / duration) || howOftenTrain || 1;
        const targetIndex = (currentWeek - 1) * daysPerWeek + (wNum - 1);
        const dayData = sortedDays[targetIndex];
        if (dayData) {
          if (dayData.weekday) {
            const key = dayData.weekday.toLowerCase().trim();
            if (DAY_LABELS[key]) return DAY_LABELS[key];
          }
          if (dayData.ymd) {
            const parts = dayData.ymd.split('-').map(Number);
            if (parts.length === 3) {
              const d = new Date(parts[0], parts[1] - 1, parts[2]);
              const full = d.toLocaleDateString('en-US', { weekday: 'long' });
              const short = d.toLocaleDateString('en-US', { weekday: 'short' });
              if (full && short) return { full, short };
            }
          }
        }
      }

      // 2. Check daysArray (draft progress) or selectedDays
      const selectedDayItem =
        daysArray?.[wNum - 1] ||
        (fetchedProgram as any)?.selectedDays?.[wNum - 1] ||
        (fetchedProgram as any)?.selected_days?.[wNum - 1];

      if (selectedDayItem) {
        const itemObj = selectedDayItem as any;
        const val = (
          typeof selectedDayItem === 'string'
            ? selectedDayItem
            : itemObj?.slug || itemObj?.value || itemObj?.title || itemObj?.label || ''
        )
          .toLowerCase()
          .trim();
        if (DAY_LABELS[val]) return DAY_LABELS[val];
      }

      // 3. Fallback based on howOftenTrain offset mapping
      const offsets = DEFAULT_OFFSETS_BY_COUNT[howOftenTrain] || DEFAULT_OFFSETS_BY_COUNT[4];
      const offset = offsets?.[wNum - 1] ?? (wNum - 1) % 7;
      return ORDERED_DAYS[offset % 7] || ORDERED_DAYS[0];
    },
    [fetchedProgram, rawApiResponse, duration, howOftenTrain, currentWeek, daysArray],
  );

  const showWorkoutSavedToast = useCallback(() => {
    notification.open({
      title: (
        <div className="custom-toast-content">
          <div className="toast-left">
            <div className="toast-header">
              <SimpleLineCheckIcon className="toast-icon" />
              <span className="toast-text">Your workout has been saved</span>
            </div>
            <div className="toast-progress-track">
              <div className="toast-progress-bar" />
            </div>
          </div>
          <div className="toast-right">
            <span
              className="toast-link"
              onClick={() => navigate('/onboarding-trainer/dashboard/programs')}
            >
              View in Library {programGeneratorSVG.header.viewInLibrary}
            </span>
          </div>
        </div>
      ),
      placement: 'topRight',
      duration: 5,
      className: 'custom-workout-toast',
      closeIcon: <div />,
    });
  }, [navigate]);

  const handleSaveProgram = async () => {
    if (!rawApiResponse || !activeClientId) return;

    const finalPlan = transformProgramStateToPlan(programData, rawApiResponse, {
      duration,
      daysPerWeek: howOftenTrain,
      selectedDays: daysArray.map((d: any) => d.slug || d.value),
      reminderTime:
        howOftenTrainReminder || getDraftProgressSlug(ProgramStepId.HowOftenTrainReminder),
      isManually,
    });
    if (!finalPlan) return;

    try {
      const clientId = activeClientId;
      const currentUser = sessionStore.getState().currentUser;
      const trainerId = currentUser?.uid;

      const payload: any = { clientId, trainerId, plan: finalPlan };

      const targetProgramId = savedProgramId || urlProgramId || draftProgramId;
      if (targetProgramId) {
        payload.programId = targetProgramId;
      }

      const response = await saveProgram(payload);
      const resolvedId = response?.programId || savedProgramId || urlProgramId || draftProgramId;
      if (resolvedId) {
        setSavedProgramId(resolvedId);
        queryClient.setQueryData(['trainer-program-plan', trainerId, resolvedId], {
          ...finalPlan,
          id: resolvedId,
          docPath: `trainer_app_users/${trainerId}/programme_plan/${resolvedId}`,
        });
        queryClient.invalidateQueries({ queryKey: ['trainer-program-plans'] });
        queryClient.invalidateQueries({ queryKey: ['trainer-client-program-tab'] });
      }

      setIsDraftSavedModalOpen(true);
    } catch (error) {
      console.error('Failed to save program', error);
      toast.error('Failed to save program');
    }
  };

  const handleAutoSave = useCallback(
    async (res: GenerateProgramResponse, newProgramData: ProgramData, clientIdToSave?: string) => {
      const finalPlan = transformProgramStateToPlan(newProgramData, res, {
        duration,
        daysPerWeek: howOftenTrain,
        selectedDays: daysArray.map((d: any) => d.slug || d.value),
        reminderTime:
          howOftenTrainReminder || getDraftProgressSlug(ProgramStepId.HowOftenTrainReminder),
        isManually,
      });
      if (!finalPlan) return null;
      try {
        const currentUser = sessionStore.getState().currentUser;
        const trainerId = currentUser?.uid;
        const targetClientId =
          clientIdToSave ||
          state?.payload?.clientId ||
          state?.clientId ||
          useProgramGeneratorProgressStore.getState().clientId ||
          (fetchedProgram as any)?.clientId;

        if (!targetClientId || !trainerId) return null;

        const payload: any = { clientId: targetClientId, trainerId, plan: finalPlan };
        const draftProgramId = useProgramGeneratorProgressStore.getState().programId;

        const targetProgramId = urlProgramId || draftProgramId;
        if (targetProgramId) {
          payload.programId = targetProgramId;
        }

        const response = await saveProgram(payload);
        const resolvedId = response?.programId || targetProgramId;
        if (resolvedId) {
          setSavedProgramId(resolvedId);

          queryClient.setQueryData(['trainer-program-plan', trainerId, resolvedId], {
            ...finalPlan,
            id: resolvedId,
            docPath: `trainer_app_users/${trainerId}/programme_plan/${resolvedId}`,
          });
          queryClient.invalidateQueries({ queryKey: ['trainer-program-plans'] });
          queryClient.invalidateQueries({ queryKey: ['trainer-client-program-tab'] });
        }
        return resolvedId;
      } catch (error) {
        console.error('Failed to auto-save program', error);
        return null;
      }
    },
    [
      saveProgram,
      state?.payload?.clientId,
      state?.clientId,
      urlProgramId,
      duration,
      howOftenTrain,
      daysArray,
      howOftenTrainReminder,
      getDraftProgressSlug,
      isManually,
      fetchedProgram,
      queryClient,
    ],
  );

  const generateProgram = (isRegenerating = false) => {
    let payload = state?.payload;
    if (!payload && isRegenerating) {
      const storedProgramId = useProgramGeneratorProgressStore.getState().programId;
      if (storedProgramId === urlProgramId) {
        payload = buildPayloadFromProgress(progress, clientId);
      } else if (fetchedProgram?.draft?.progress) {
        payload = buildPayloadFromProgress(fetchedProgram.draft.progress, fetchedProgram.clientId);
      }
    }

    if (!payload) {
      setIsRegenerateModalOpen(false);
      return;
    }

    setIsGeneratingProgram(true);

    // Clear the existing data if we are regenerating
    if (isRegenerating) {
      setProgramData({});
      setSavedProgramId(null);
    }

    fetchPromise.current = mutateAsync(payload);
    fetchPromise.current
      .then(async (res) => {
        if (!res) {
          setIsGeneratingProgram(false);
          toast.error('Program generation failed. Please try again.');
          return;
        }
        setRawApiResponse(res);
        const newProgramData = transformApiToProgramData(res, howOftenTrain);
        setProgramData(newProgramData);
        setIsGeneratingProgram(false);
        const savedId = await handleAutoSave(res, newProgramData);
        if (isRegenerating) setIsRegenerateModalOpen(false);
        const meta = res?.metadata || res?.result?.metadata;
        const generatedId =
          savedId || meta?.programId || meta?.program_id || res?.program?.id || urlProgramId;
        if (generatedId) {
          setSavedProgramId(generatedId);
          navigate(`/onboarding-trainer/dashboard/program-generated/${generatedId}`, {
            replace: true,
            state: {},
          });
        }
      })
      .catch(() => {
        setIsGeneratingProgram(false);
        toast.error('Program generation failed. Please try again.');
      });
  };

  useEffect(() => {
    let isMounted = true;
    if (state?.payload) {
      if (!fetchPromise.current) {
        setIsGeneratingProgram(true);
        fetchPromise.current = mutateAsync(state.payload);
      } else {
        // If it's already fetching from a previous mount, just set loading to true
        setIsGeneratingProgram(true);
      }

      fetchPromise.current
        .then(async (res) => {
          if (!res) {
            if (isMounted) {
              setIsGeneratingProgram(false);
              toast.error('Program generation failed. Please try again.');
            }
            return;
          }
          if (isMounted) {
            setRawApiResponse(res);
            const newProgramData = transformApiToProgramData(res, howOftenTrain);
            setProgramData(newProgramData);
            setIsGeneratingProgram(false);
            const savedId = await handleAutoSave(res, newProgramData);
            const meta = res?.metadata || res?.result?.metadata;
            const generatedId =
              savedId || meta?.programId || meta?.program_id || res?.program?.id || urlProgramId;
            if (generatedId && isMounted) {
              setSavedProgramId(generatedId);
              navigate(`/onboarding-trainer/dashboard/program-generated/${generatedId}`, {
                replace: true,
                state: {},
              });
            }
          }
        })
        .catch(() => {
          if (isMounted) {
            setIsGeneratingProgram(false);
            toast.error('Program generation failed. Please try again.');
          }
        });
    }

    return () => {
      isMounted = false;
    };
  }, [state?.payload, mutateAsync, howOftenTrain, navigate, handleAutoSave, urlProgramId]);

  const { clientId } = useProgramGeneratorProgressStore();

  useEffect(() => {
    if (!state?.payload && fetchedProgram && !isGeneratingProgram) {
      // We loaded the program from the API using URL id
      const transformedData = transformFinalPlanToProgramData(fetchedProgram, howOftenTrain);

      const hasWorkouts = Object.keys(transformedData).length > 0;
      if (!hasWorkouts) {
        // The program is empty (draft)
        const storedProgramId = useProgramGeneratorProgressStore.getState().programId;
        if (storedProgramId === urlProgramId) {
          // This draft matches the current wizard session! We should auto-generate it!
          const payload = buildPayloadFromProgress(progress, clientId);
          if (payload) {
            if (!fetchPromise.current) {
              setIsGeneratingProgram(true);
              fetchPromise.current = mutateAsync(payload);
              fetchPromise.current
                .then(async (res) => {
                  if (!res) {
                    setIsGeneratingProgram(false);
                    toast.error('Program generation failed. Please try again.');
                    return;
                  }
                  setRawApiResponse(res);
                  const newProgramData = transformApiToProgramData(res, howOftenTrain);
                  setProgramData(newProgramData);
                  setIsGeneratingProgram(false);
                  await handleAutoSave(res, newProgramData, clientId || undefined);
                })
                .catch(() => {
                  setIsGeneratingProgram(false);
                  toast.error('Program generation failed. Please try again.');
                });
            }
            return;
          }
        }
      }

      setProgramData(transformedData);

      // Reconstruct rawApiResponse partially if needed for save/update logic later
      // The current UI relies on rawApiResponse for the transformer.
      setRawApiResponse({
        result: {
          program: {
            data: {
              ...fetchedProgram,
              duration: fetchedProgram?.duration || String(duration),
              isAIGenerate: isManually ? false : (fetchedProgram?.isAIGenerate ?? true),
              isManually,
              programmeName:
                fetchedProgram?.programmeName && fetchedProgram?.programmeName !== 'Unnamed'
                  ? fetchedProgram.programmeName
                  : progress?.program_name?.[0]?.value ||
                    fetchedProgram?.draft?.progress?.program_name?.[0]?.value ||
                    'Unnamed',
            },
          },
        },
      } as unknown as GenerateProgramResponse);
    }
  }, [
    fetchedProgram,
    state?.payload,
    howOftenTrain,
    isGeneratingProgram,
    urlProgramId,
    progress,
    clientId,
    mutateAsync,
    handleAutoSave,
    isManually,
    duration,
    navigate,
  ]);

  const sections = programData[currentWeek]?.[currentWorkout] || {
    warmup: [],
    main: [],
    cooldown: [],
  };

  const updateSection = (sectionId: string, updater: (prev: ExerciseData[]) => ExerciseData[]) => {
    setProgramData((prevData) => {
      const weekData = prevData[currentWeek] || {};
      const workoutData = weekData[currentWorkout] || { warmup: [], main: [], cooldown: [] };
      const prevSection = workoutData[sectionId as keyof SectionData] || [];
      const newSection = updater(prevSection);

      return {
        ...prevData,
        [currentWeek]: {
          ...weekData,
          [currentWorkout]: {
            ...workoutData,
            [sectionId]: newSection,
          },
        },
      };
    });
  };

  const [copiedContent, setCopiedContent] = useState<ExerciseData[] | null>(null);
  const [generatingExerciseId] = useState<string | null>(null);

  const handleAddExerciseClick = () => {
    if (!isLibraryOpen) {
      setIsLibraryOpen(true);
    }

    setTimeout(() => {
      const exerciseListEl = document.querySelector('.exercise-list');
      if (exerciseListEl) {
        exerciseListEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        exerciseListEl.classList.add('highlight-exercise-list');
        setTimeout(() => {
          exerciseListEl.classList.remove('highlight-exercise-list');
        }, 7000);
      }
    }, 300);
  };

  const handleSaveExercise = () => {
    if (editingExercise) {
      if (
        !editingExercise.ex.isRest &&
        (editForm.sets < 1 ||
          (editForm.metricType === 'reps' && editForm.reps < 1) ||
          (editForm.metricType === 'duration' && editForm.duration < 1))
      ) {
        toast.error('Sets and Reps/Duration must be greater than 0');
        return;
      }
      updateSection(editingExercise.sectionId, (prev) =>
        prev.map((e) => (e.id === editingExercise.ex.id ? { ...e, ...editForm } : e)),
      );
    }
    setIsEditModalOpen(false);
  };

  const getExerciseMenu = (ex: ExerciseData, sectionId: string): MenuProps['items'] => {
    if (ex.isRest) {
      return [
        {
          key: 'duplicate',
          label: 'Duplicate',
          icon: programGeneratorSVG.exerciseAction.duplicate,
          onClick: () => {
            updateSection(sectionId, (prev) => {
              const index = prev.findIndex((e) => e.id === ex.id);
              if (index === -1) return prev;
              const newEx = { ...ex, id: `rest-${Date.now()}-${Math.random()}` };
              const newSectionExercises = [...prev];
              newSectionExercises.splice(index + 1, 0, newEx);
              return newSectionExercises;
            });
          },
        },
        {
          key: 'remove',
          label: 'Remove',
          icon: programGeneratorSVG.exerciseAction.remove,
          onClick: () => {
            setExerciseToRemove({ sectionId, exId: ex.id });
          },
        },
      ];
    }

    return [
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: programGeneratorSVG.exerciseAction.duplicate,
        onClick: () => {
          updateSection(sectionId, (prev) => {
            const index = prev.findIndex((e) => e.id === ex.id);
            if (index === -1) return prev;
            const newEx = { ...ex, id: `${ex.name}-${Date.now()}-${Math.random()}` };
            const newSectionExercises = [...prev];
            newSectionExercises.splice(index + 1, 0, newEx);
            return newSectionExercises;
          });
        },
      },
      // {
      //   key: 'replace',
      //   label: 'Replace exercise',
      //   icon: programGeneratorSVG.exerciseAction.replaceExercise,
      //   onClick: () => {
      //     setGeneratingExerciseId(ex.id);
      //     setTimeout(() => {
      //       setGeneratingExerciseId(null);
      //     }, 10000);
      //   },
      // },
      // {
      //   key: 'superset',
      //   label: ex.supersetId ? 'Convert to single exercise' : 'Convert to superset',
      //   icon: programGeneratorSVG.exerciseAction.convertToSuperset,
      //   onClick: () => {
      //     updateSection(sectionId, (prev) => {
      //       const index = prev.findIndex((e) => e.id === ex.id);
      //       if (index === -1) return prev;
      //       const newSectionExercises = [...prev];

      //       if (ex.supersetId) {
      //         newSectionExercises[index] = { ...ex, supersetId: undefined };
      //       } else {
      //         const newSupersetId = `superset-${Date.now()}`;
      //         newSectionExercises[index] = { ...ex, supersetId: newSupersetId };
      //         newSectionExercises.splice(index + 1, 0, {
      //           id: `rest-${Date.now()}`,
      //           name: 'Rest',
      //           tags: [],
      //           isRest: true,
      //           restDuration: '30 sec',
      //           supersetId: newSupersetId,
      //         });
      //       }
      //       return newSectionExercises;
      //     });
      //   },
      // },
      {
        key: 'remove',
        label: 'Remove',
        icon: programGeneratorSVG.exerciseAction.remove,
        onClick: () => {
          setExerciseToRemove({ sectionId, exId: ex.id });
        },
      },
    ];
  };

  const getSectionMenu = (sectionId: string, exercises: ExerciseData[]): MenuProps['items'] => {
    const isEmpty = exercises.length === 0;
    const canPaste = copiedContent !== null && copiedContent.length > 0;

    return [
      {
        key: 'copy',
        label: 'Copy content',
        icon: programGeneratorSVG.exerciseAction.duplicate,
        disabled: isEmpty,
        onClick: () => setCopiedContent(exercises),
      },
      {
        key: 'paste',
        label: 'Paste content',
        icon: programGeneratorSVG.exerciseAction.pasteContent,
        disabled: !canPaste,
        onClick: () => {
          if (copiedContent) {
            updateSection(sectionId, (prev) => [
              ...prev,
              ...copiedContent.map((ex) => ({
                ...ex,
                id: `${ex.name}-${Date.now()}-${Math.random()}`,
              })),
            ]);
          }
        },
      },
      // {
      //   key: 'replace',
      //   label: 'Replace exercises',
      //   icon: programGeneratorSVG.exerciseAction.replaceExercise,
      // },
      // {
      //   key: 'superset',
      //   label: 'Convert to superset',
      //   icon: programGeneratorSVG.exerciseAction.convertToSuperset,
      //   disabled: exercises.every((ex) => ex.supersetId),
      //   onClick: () => {
      //     updateSection(sectionId, (prev) => {
      //       const newSupersetId = `superset-${Date.now()}`;
      //       const newExercises = prev.map((ex) =>
      //         ex.supersetId ? ex : { ...ex, supersetId: newSupersetId },
      //       );

      //       // Auto add a rest card at the very end if we converted anything
      //       const hasConverted = prev.some((ex) => !ex.supersetId);
      //       if (hasConverted) {
      //         newExercises.push({
      //           id: `rest-${Date.now()}`,
      //           name: 'Rest',
      //           tags: [],
      //           isRest: true,
      //           restDuration: '30 sec',
      //           supersetId: newSupersetId,
      //         });
      //       }
      //       return newExercises;
      //     });
      //   },
      // },
      {
        key: 'empty',
        label: 'Empty content',
        icon: programGeneratorSVG.exerciseAction.remove,
        disabled: isEmpty,
        onClick: () => {
          setSectionToEmpty({ sectionId });
        },
      },
    ];
  };

  const getFlatIndexForDrop = (
    arr: ExerciseData[] | undefined,
    sectionId: string,
    droppableId: string,
    localIndex: number,
  ) => {
    if (!arr) return -1;

    let count = 0;
    let lastSeenGlobalIndex = -1;

    for (let i = 0; i < arr.length; i++) {
      const item = arr[i];
      let itemDroppableId = sectionId;
      if (item.supersetId) {
        itemDroppableId = `superset-drop-${sectionId}-${item.supersetId}`;
      }

      if (itemDroppableId === droppableId) {
        if (count === localIndex) return i;
        count++;
        lastSeenGlobalIndex = i;
      }
    }

    if (count === localIndex) {
      if (lastSeenGlobalIndex !== -1) {
        return lastSeenGlobalIndex + 1;
      } else {
        return arr.length;
      }
    }
    return arr.length;
  };

  const handleDragStart = (_start: DragStart) => {
    // Keep sidebar layout stable during drag to preserve droppable coordinate precision
  };

  const handleDragEnd = (result: DropResult) => {
    console.log('calling DRAG END', result);
    if (!result.destination) return;
    const { source, destination, draggableId } = result;

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const parseDroppableId = (dId: string) => {
      if (dId === 'library') return { sectionId: 'library', supersetId: undefined };
      if (dId.startsWith('superset-drop-')) {
        const parts = dId.replace('superset-drop-', '').split('-');
        return { sectionId: parts[0], supersetId: parts.slice(1).join('-') };
      }
      return { sectionId: dId, supersetId: undefined };
    };

    const src = parseDroppableId(source.droppableId);
    const dest = parseDroppableId(destination.droppableId);

    // 1. Dropping from Library
    if (src.sectionId === 'library') {
      const moveId = draggableId.replace('lib-', '');
      const isRest = moveId === 'rest-library-item';

      const movement = movementsData.find((m) => m.id === moveId);

      const exerciseName = isRest ? 'Rest' : movement?.en_display_name || 'Exercise';

      const tags: any[] = [];
      if (!isRest && movement) {
        if (movement.move_types) {
          movement.move_types.forEach((t) => tags.push({ label: t, type: 'blue' }));
        }
        if (movement.tags) {
          movement.tags.forEach((t) => tags.push({ label: t, type: 'purple' }));
        }
      } else if (isRest) {
        tags.push({ label: 'Rest', type: 'yellow' });
      }

      const tagLabels = tags.map((t: any) => t.label.toLowerCase());
      const expectedTag =
        dest.sectionId === 'cooldown'
          ? 'cooldown'
          : dest.sectionId === 'warmup'
            ? 'warmup'
            : 'main';

      if (!isRest && !tagLabels.includes(expectedTag)) {
        const typeTags = tags.filter((t: any) => t.type !== 'yellow').map((t: any) => t.label);
        const sectionDisplay = expectedTag.charAt(0).toUpperCase() + expectedTag.slice(1);
        toast.error(
          `${exerciseName} is ideally for ${typeTags.join('/')}, but I've placed it in ${sectionDisplay} for you.`,
          { style: { marginTop: '10vh' } },
        );
      }

      const newExercise: ExerciseData = {
        id: `${exerciseName}-${Date.now()}`,
        name: exerciseName,
        tags: tags,
        image_male_url: isRest ? '' : movement?.image_male_url,
        image_female_url: isRest ? '' : movement?.image_female_url,
        isRest: isRest,
        restDuration: isRest ? '30 sec' : undefined,
        supersetId: dest.supersetId,
      };

      updateSection(dest.sectionId, (prev) => {
        const targetGlobalIndex = getFlatIndexForDrop(
          prev,
          dest.sectionId,
          destination.droppableId,
          destination.index,
        );
        const newArr = [...prev];
        if (targetGlobalIndex >= 0 && targetGlobalIndex <= newArr.length) {
          newArr.splice(targetGlobalIndex, 0, newExercise);
        } else {
          newArr.push(newExercise);
        }
        return newArr;
      });
      if (window.innerWidth < 1024) {
        setIsLibraryOpen(false);
      }
      return;
    }

    // 2. Reordering within same or different section
    if (src.sectionId === dest.sectionId) {
      updateSection(src.sectionId, (prev) => {
        const sourceGlobalIndex = getFlatIndexForDrop(
          prev,
          src.sectionId,
          source.droppableId,
          source.index,
        );

        let destGlobalIndex = getFlatIndexForDrop(
          prev,
          dest.sectionId,
          destination.droppableId,
          destination.index,
        );

        // Safety checks to prevent crashes if indices are invalid
        if (sourceGlobalIndex === -1 || sourceGlobalIndex >= prev.length) return prev;

        if (destGlobalIndex === -1) {
          destGlobalIndex = Math.min(destination.index, prev.length - 1);
        }

        const newArr = [...prev];
        const [movedItem] = newArr.splice(sourceGlobalIndex, 1);
        if (!movedItem) return prev; // Extra safety

        movedItem.supersetId = dest.supersetId;

        // If dragging across different droppables (e.g. from main list into a superset),
        // we must manually subtract 1 from destGlobalIndex if the source item was removed from
        // a preceding global position, because the library's destination.index only accounts
        // for the local shift within the isolated destination droppable.
        if (source.droppableId !== destination.droppableId && sourceGlobalIndex < destGlobalIndex) {
          destGlobalIndex -= 1;
        }

        newArr.splice(destGlobalIndex, 0, movedItem);
        return newArr;
      });
    } else {
      // Prevent moving between different sections
      toast.error('Exercises cannot be moved between Warm Up, Main Movements, and Cool Downs.');
      return;
    }
  };

  const renderSectionExercises = (exercises: ExerciseData[], sectionId: string) => {
    if (exercises.length === 0) return renderEmptyState('Drag exercises here');

    const elements: React.ReactNode[] = [];
    let i = 0;
    let supersetCount = 0;
    let droppableIndex = 0;

    while (i < exercises.length) {
      const ex = exercises[i];
      if (ex.supersetId) {
        const startIndex = i;
        const group: ExerciseData[] = [];
        const currentSupersetId = ex.supersetId;
        while (i < exercises.length && exercises[i].supersetId === currentSupersetId) {
          group.push(exercises[i]);
          i++;
        }

        const groupLetter = String.fromCharCode(65 + supersetCount);
        supersetCount++;

        elements.push(
          <div key={`superset-group-${currentSupersetId}-${startIndex}`} className="superset-group">
            <div className="superset-header">
              <div className="superset-title">
                SUPERSET {groupLetter} &nbsp;&nbsp;
                <span>
                  3 rounds &bull; {group.filter((ex) => !ex.isRest).length} exercises &bull;{' '}
                  {getSectionDuration(group) * 3} min
                </span>
              </div>
              <div className="superset-actions">
                <div
                  className={`collapse-icon ${collapsedSupersets[currentSupersetId] ? 'collapsed' : ''}`}
                  onClick={() =>
                    setCollapsedSupersets((prev) => ({
                      ...prev,
                      [currentSupersetId]: !prev[currentSupersetId],
                    }))
                  }
                >
                  {programGeneratorSVG.weekProgram.weekDropdownArrow}
                </div>
                <Dropdown
                  menu={{ items: getSectionMenu(sectionId, group) }}
                  trigger={['click']}
                  placement="bottomRight"
                  classNames={{ root: 'action-dropdown-menu-overlay' }}
                >
                  <button className="icon-btn">{programGeneratorSVG.exerciseAction.menu}</button>
                </Dropdown>
              </div>
            </div>

            <DroppableSuperset
              id={`superset-drop-${sectionId}-${currentSupersetId}`}
              className={collapsedSupersets[currentSupersetId] ? 'collapsed' : ''}
            >
              {(() => {
                let exerciseIndex = 0;
                return group.map((gEx, _gIdx) => {
                  if (gEx.isRest) {
                    return (
                      <SortableRestCard
                        key={gEx.id}
                        item={gEx}
                        index={_gIdx}
                        sectionId={sectionId}
                        getExerciseMenu={getExerciseMenu}
                        onDoubleClickMetrics={() => {
                          setEditingExercise({ ex: gEx, sectionId });
                          setIsEditModalOpen(true);
                        }}
                        isEditing={
                          isEditModalOpen &&
                          editingExercise?.ex.id === gEx.id &&
                          editingExercise?.sectionId === sectionId
                        }
                        editForm={editForm}
                        setEditForm={setEditForm}
                        onEditCancel={() => {
                          handleSaveExercise();
                          setIsEditModalOpen(false);
                        }}
                      />
                    );
                  }

                  exerciseIndex++;
                  return (
                    <SortableExerciseCard
                      key={gEx.id}
                      ex={gEx}
                      index={_gIdx}
                      sectionId={sectionId}
                      gender={gender}
                      getExerciseMenu={getExerciseMenu}
                      isGenerating={generatingExerciseId === gEx.id}
                      supersetLabel={`${groupLetter}${exerciseIndex}`}
                      onDoubleClickMetrics={() => {
                        setEditingGroupLetter(groupLetter);
                        setIsEditSupersetOpen(true);
                      }}
                      isEditing={
                        isEditModalOpen &&
                        editingExercise?.ex.id === gEx.id &&
                        editingExercise?.sectionId === sectionId
                      }
                      focusField={editFocusField}
                      editForm={editForm}
                      setEditForm={setEditForm}
                      onEditCancel={() => {
                        handleSaveExercise();
                        setIsEditModalOpen(false);
                      }}
                    />
                  );
                });
              })()}

              <button className="add-exercise-btn" onClick={handleAddExerciseClick}>
                + Add exercise
              </button>
            </DroppableSuperset>
          </div>,
        );
      } else {
        const cardIndex = droppableIndex++;
        if (ex.isRest) {
          elements.push(
            <SortableRestCard
              key={ex.id}
              item={ex}
              index={cardIndex}
              sectionId={sectionId}
              getExerciseMenu={getExerciseMenu}
              onDoubleClickMetrics={() => {
                setEditingExercise({ ex, sectionId });
                setIsEditModalOpen(true);
              }}
              isEditing={
                isEditModalOpen &&
                editingExercise?.ex.id === ex.id &&
                editingExercise?.sectionId === sectionId
              }
              editForm={editForm}
              setEditForm={setEditForm}
              onEditCancel={() => {
                handleSaveExercise();
                setIsEditModalOpen(false);
              }}
            />,
          );
        } else {
          elements.push(
            <SortableExerciseCard
              key={ex.id}
              ex={ex}
              index={cardIndex}
              sectionId={sectionId}
              gender={gender}
              getExerciseMenu={getExerciseMenu}
              isGenerating={generatingExerciseId === ex.id}
              onDoubleClickMetrics={(field = 'reps') => {
                setEditFocusField(field);
                setEditingExercise({ ex, sectionId });
                setIsEditModalOpen(true);
              }}
              isEditing={
                isEditModalOpen &&
                editingExercise?.ex.id === ex.id &&
                editingExercise?.sectionId === sectionId
              }
              focusField={editFocusField}
              editForm={editForm}
              setEditForm={setEditForm}
              onEditCancel={() => {
                handleSaveExercise();
                setIsEditModalOpen(false);
              }}
            />,
          );
        }
        i++;
      }
    }

    return <>{elements}</>;
  };

  const renderEmptyState = (text: string) => (
    <div
      style={{
        color: '#e5e5e5',
        opacity: 0.4,
        textAlign: 'center',
        padding: '20px 0',
        fontSize: '13px',
      }}
    >
      {text}
    </div>
  );

  return (
    <>
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="pg-layout-container">
          {urlWorkout ? (
            <header className="workout-view-header">
              <div className="wvh-top">
                <div className="wvh-title-row">
                  <div
                    className="back-btn"
                    onClick={() =>
                      navigate(`/onboarding-trainer/dashboard/program-generated/${urlProgramId}`)
                    }
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </div>
                  <h2>
                    {fetchedProgram?.programmeName && fetchedProgram?.programmeName !== 'Unnamed'
                      ? fetchedProgram.programmeName
                      : progress?.program_name?.[0]?.value ||
                        fetchedProgram?.draft?.progress?.program_name?.[0]?.value ||
                        '1st program'}
                    {"'s workout"}
                    <span className="dot">•</span>
                    <span className="date-sub">Monday, May 26</span>
                  </h2>
                </div>
                <div className="wvh-actions">
                  <Button
                    variant="light"
                    className="btn-save-white"
                    onClick={handleSaveProgram}
                    isLoading={isSaving}
                    disabled={isGeneratingProgram}
                  >
                    Save
                  </Button>
                </div>
              </div>
              <div className="wvh-badges">
                <div className="workout-badge">
                  <GoalApproachIcon /> {getDisplayTitle(goalApproach || '')}
                </div>
                <div className="workout-badge">
                  <EnergyLevelIcon /> {mapEnergyLevel(energyLevel)}
                </div>
                <div className="workout-badge">
                  <DurationIcon />{' '}
                  <span className="badge">
                    {workoutDuration ? `${workoutDuration.replace('-min', '')} Mins` : '45 Mins'}
                  </span>
                </div>
                <div className="workout-badge">
                  <FocusIcon />{' '}
                  {trainingGoal ? getDisplayTitle(trainingGoal).toUpperCase() : 'GENERAL FITNESS'}
                </div>
              </div>
            </header>
          ) : (
            <header className="pg-header">
              <div className="header-client-info">
                <div className="client-avatar">
                  {clientData?.clients?.[0]?.avatarUrl ? (
                    <img
                      src={clientData.clients[0].avatarUrl}
                      alt={clientName}
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    getInitials(clientName)
                  )}
                </div>
                <div className="client-details">
                  <h2 className="client-name">{clientName}</h2>
                  <div className="client-meta">
                    {duration} weeks &bull; {howOftenTrain} days
                  </div>
                </div>
              </div>

              {showHeaderStats && (
                <div className="header-stats">
                  <div className="stat-block">
                    <span className="stat-label">Experience Level:</span>
                    <span className="stat-value">
                      <span className="stat-text">{experienceLevel}</span>
                      <div className="dots">
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className={i < dotCount ? 'active' : ''}></span>
                        ))}
                      </div>
                    </span>
                  </div>
                  <div className="stat-block">
                    <span className="stat-label">Program Goal:</span>
                    <span className="stat-value" style={{ textTransform: 'capitalize' }}>
                      <span className="stat-text">{toTitleCase(programGoal)}</span>
                    </span>
                  </div>
                  <div className="stat-block">
                    <span className="stat-label">Pain/Injuries:</span>
                    <span className="stat-value" style={{ textTransform: 'capitalize' }}>
                      <span className="stat-text">{toTitleCase(painInjuries)}</span>
                    </span>
                  </div>
                </div>
              )}

              <div className="header-actions">
                {!activeClientId && !isFetchingProgram && !isGeneratingProgram && (
                  <Button
                    variant="outline"
                    className="btn-assign"
                    onClick={() => setIsAssignModalOpen(true)}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {programGeneratorSVG.header.assignToClient} Assign to client
                    </span>
                  </Button>
                )}
                {showRegenerateBtn && (
                  <Button
                    variant="outline"
                    className="regenerate-btn"
                    onClick={() => setIsRegenerateModalOpen(true)}
                    disabled={isGeneratingProgram}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {programGeneratorSVG.header.regenerateWorkout} Regenerate Workout
                    </span>
                  </Button>
                )}
                <Button
                  variant="light"
                  className="save-btn"
                  onClick={handleSaveProgram}
                  isLoading={isSaving}
                  disabled={isGeneratingProgram}
                >
                  Save
                </Button>
              </div>
            </header>
          )}

          <div className={`pg-body ${isLibraryOpen ? 'library-open' : ''}`}>
            <ExerciseLibrary
              gender={gender}
              isOpen={isLibraryOpen}
              onClose={() => setIsLibraryOpen(false)}
            />

            <div className="pg-main" ref={pgMainRef}>
              <button className="lib-trigger-btn" onClick={() => setIsLibraryOpen((prev) => !prev)}>
                {programGeneratorSVG?.sidebar.toggle}
              </button>

              <div className="main-header">
                <div className="main-stat">
                  {programGeneratorSVG.calender} {duration}-week program
                </div>
                <div className="main-stat">{howOftenTrain} workouts / week</div>
              </div>

              <div className="main-content">
                {isGeneratingProgram || isFetchingProgram ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      minHeight: '400px',
                    }}
                  >
                    <h3 style={{ color: '#fff' }}>
                      {isGeneratingProgram ? 'Generating AI Program...' : 'Loading Program...'}
                    </h3>
                  </div>
                ) : (
                  <>
                    <div className="program-view-header">
                      <div className="week-selector">
                        <button
                          className="week-nav-btn"
                          onClick={handlePrevWeek}
                          disabled={currentWeek === 1}
                        >
                          {programGeneratorSVG.weekProgram.prevWeek}
                        </button>

                        <div className="week-dropdown-container">
                          <Dropdown
                            menu={{ items: weekItems }}
                            trigger={['click']}
                            classNames={{ root: 'week-dropdown-menu-overlay' }}
                            placement="bottom"
                          >
                            <button className="week-dropdown-btn">
                              <span>
                                <strong>Week {currentWeek}</strong> of {duration}
                              </span>
                              <span className="dropdown-arrow">
                                {programGeneratorSVG.weekProgram.weekDropdownArrow}
                              </span>
                            </button>
                          </Dropdown>
                        </div>

                        <button
                          className="week-nav-btn"
                          onClick={handleNextWeek}
                          disabled={currentWeek === duration}
                        >
                          {programGeneratorSVG.weekProgram.nextWeek}
                        </button>
                      </div>

                      <h3 className="current-workout-title">
                        Week {currentWeek} &bull; Workout {currentWorkout}
                      </h3>

                      <div className="workout-tabs">
                        {Array.from({ length: howOftenTrain }).map((_, i) => {
                          const wNum = i + 1;
                          const dayLabel = getWorkoutDayOfWeek(wNum);
                          return (
                            <button
                              key={wNum}
                              className={`workout-tab ${currentWorkout === wNum ? 'active' : ''}`}
                              onClick={() => setCurrentWorkout(wNum)}
                            >
                              <strong>
                                <span className="desktop-text">Workout {wNum}</span>
                                <span className="mobile-text">W{wNum}</span>
                              </strong>
                              <span className="workout-day-text">
                                <span className="desktop-day">{dayLabel.full}</span>
                                <span className="mobile-day">{dayLabel.short}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="program-view-body">
                      {/* WARM UP */}
                      <WorkoutSection id="warmup" className="workout-section">
                        <div className="section-header">
                          <div className="header-left">
                            <div className="section-icon">
                              {programGeneratorSVG.exerciseType.warmup}
                            </div>
                            <div className="header-info">
                              <h4>WARM UP</h4>
                              <span>
                                {sections.warmup.filter((ex) => !ex.isRest).length} exercises &bull;{' '}
                                {getSectionDuration(sections.warmup)} min
                              </span>
                            </div>
                          </div>
                          <div className="header-center desktop-only">
                            <div className="metrics-grid">
                              <div className="metrics-grid-info">
                                Sets &middot; Repetitions / Time &middot; Rest
                              </div>
                            </div>
                          </div>
                          <div className="header-right">
                            <Dropdown
                              menu={{ items: getSectionMenu('warmup', sections.warmup) }}
                              trigger={['click']}
                              placement="bottomRight"
                              classNames={{ root: 'action-dropdown-menu-overlay' }}
                            >
                              <button className="icon-btn">
                                {programGeneratorSVG.exerciseAction.menu}
                              </button>
                            </Dropdown>
                          </div>
                        </div>
                        <div className="section-content">
                          <DroppableExercisesList id="warmup">
                            {renderSectionExercises(sections.warmup, 'warmup')}
                          </DroppableExercisesList>
                          <button className="add-exercise-btn" onClick={handleAddExerciseClick}>
                            + Add exercise
                          </button>
                        </div>
                      </WorkoutSection>

                      <WorkoutSection id="main" className="workout-section">
                        <div className="section-header">
                          <div className="header-left">
                            <div className="section-icon">
                              {programGeneratorSVG.exerciseType.main}
                            </div>
                            <div className="header-info">
                              <h4>MAIN MOVEMENTS</h4>
                              <span>
                                {sections.main.filter((ex) => !ex.isRest).length} exercises &bull;{' '}
                                {getSectionDuration(sections.main)} min
                              </span>
                            </div>
                          </div>
                          <div className="header-center desktop-only">
                            <div className="metrics-grid">
                              <div className="metrics-grid-info">
                                Sets &middot; Repetitions / Time &middot; Rest
                              </div>
                            </div>
                          </div>
                          <div className="header-right">
                            <Dropdown
                              menu={{ items: getSectionMenu('main', sections.main) }}
                              trigger={['click']}
                              placement="bottomRight"
                              classNames={{ root: 'action-dropdown-menu-overlay' }}
                            >
                              <button className="icon-btn">
                                {programGeneratorSVG.exerciseAction.menu}
                              </button>
                            </Dropdown>
                          </div>
                        </div>
                        <div className="section-content">
                          <DroppableExercisesList id="main">
                            {renderSectionExercises(sections.main, 'main')}
                          </DroppableExercisesList>
                          <button className="add-exercise-btn" onClick={handleAddExerciseClick}>
                            + Add exercise
                          </button>
                        </div>
                      </WorkoutSection>

                      {/* COOL DOWNS */}
                      <WorkoutSection id="cooldown" className="workout-section">
                        <div className="section-header">
                          <div className="header-left">
                            <div className="section-icon">
                              {programGeneratorSVG.exerciseType.cooldown}
                            </div>
                            <div className="header-info">
                              <h4>COOL DOWNS</h4>
                              <span>
                                {sections.cooldown.filter((ex) => !ex.isRest).length} exercises
                                &bull; {getSectionDuration(sections.cooldown)} min
                              </span>
                            </div>
                          </div>
                          <div className="header-center desktop-only">
                            <div className="metrics-grid">
                              <div className="metrics-grid-info">
                                Sets &middot; Repetitions / Time &middot; Rest
                              </div>
                            </div>
                          </div>
                          <div className="header-right">
                            <Dropdown
                              menu={{ items: getSectionMenu('cooldown', sections.cooldown) }}
                              trigger={['click']}
                              placement="bottomRight"
                              classNames={{ root: 'action-dropdown-menu-overlay' }}
                            >
                              <button className="icon-btn">
                                {programGeneratorSVG.exerciseAction.menu}
                              </button>
                            </Dropdown>
                          </div>
                        </div>
                        <div className="section-content">
                          <DroppableExercisesList id="cooldown">
                            {renderSectionExercises(sections.cooldown, 'cooldown')}
                          </DroppableExercisesList>
                          <button className="add-exercise-btn" onClick={handleAddExerciseClick}>
                            + Add exercise
                          </button>
                        </div>
                      </WorkoutSection>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <Modal
            open={!!exerciseToRemove}
            footer={null}
            onCancel={() => setExerciseToRemove(null)}
            centered
            width={518}
            closeIcon={null}
            rootClassName="logout-modal"
            className="logout-modal"
            styles={{
              mask: {
                backdropFilter: 'blur(4px)',
                backgroundColor: 'rgba(6, 7, 35, 0.8)',
              },
            }}
          >
            <div className="logout-modal-content">
              <div className="logout-icon-wrapper">{<DashboardModalDeleteIcon />}</div>
              <h2>Remove Exercise?</h2>
              <p>Are you sure you want to remove this exercise?</p>

              <div className="logout-actions">
                <button
                  className="btn-logout"
                  onClick={() => {
                    if (exerciseToRemove) {
                      updateSection(exerciseToRemove.sectionId, (prev) =>
                        prev.filter((e) => e.id !== exerciseToRemove.exId),
                      );
                      setExerciseToRemove(null);
                    }
                  }}
                >
                  Remove
                </button>
                <button className="btn-cancel" onClick={() => setExerciseToRemove(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </Modal>

          <Modal
            open={!!sectionToEmpty}
            footer={null}
            onCancel={() => setSectionToEmpty(null)}
            centered
            width={518}
            closeIcon={null}
            rootClassName="logout-modal"
            className="logout-modal"
            styles={{
              mask: {
                backdropFilter: 'blur(4px)',
                backgroundColor: 'rgba(6, 7, 35, 0.8)',
              },
            }}
          >
            <div className="logout-modal-content">
              <div className="logout-icon-wrapper">{<DashboardModalDeleteIcon />}</div>
              <h2>Empty Content?</h2>
              <p>Are you sure you want to empty the content of this section?</p>

              <div className="logout-actions">
                <button
                  className="btn-logout"
                  onClick={() => {
                    if (sectionToEmpty) {
                      updateSection(sectionToEmpty.sectionId, () => []);
                      setSectionToEmpty(null);
                    }
                  }}
                >
                  Empty Content
                </button>
                <button className="btn-cancel" onClick={() => setSectionToEmpty(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </Modal>

          <Modal
            open={isRegenerateModalOpen}
            footer={null}
            onCancel={() => setIsRegenerateModalOpen(false)}
            centered
            width={518}
            closeIcon={null}
            rootClassName="logout-modal regenerate-modal"
            className="logout-modal"
            styles={{
              mask: {
                backdropFilter: 'blur(4px)',
                backgroundColor: 'rgba(6, 7, 35, 0.8)',
              },
            }}
          >
            <div className="logout-modal-content">
              <div
                className="logout-icon-wrapper"
                style={{
                  width: '80px',
                  height: '80px',
                  background: 'transparent',
                  position: 'relative',
                }}
              >
                <video
                  src={MiniPTGIF}
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <span className="minipt-text">miniPT</span>
              </div>
              <h2>
                {' '}
                Are you sure you want to regenerate the program? All manual edits will be lost
              </h2>

              <div className="logout-actions">
                <Button
                  className="btn-logout"
                  onClick={() => generateProgram(true)}
                  isLoading={isGeneratingProgram}
                  disabled={isGeneratingProgram}
                >
                  Yes, Regenerate
                </Button>
                <button className="btn-cancel" onClick={() => setIsRegenerateModalOpen(false)}>
                  {programGeneratorSVG.exerciseAction.backToProgram} Back to Program
                </button>
              </div>
            </div>
          </Modal>
        </div>
      </DragDropContext>

      <Modal
        title={`Edit superset ${editingGroupLetter}`}
        open={isEditSupersetOpen}
        onCancel={() => setIsEditSupersetOpen(false)}
        footer={null}
        centered
        width={611}
        rootClassName="pg-edit-modal pg-edit-superset"
        closeIcon={programGeneratorSVG.EditExercise.close}
      >
        <div className="pg-modal-content">
          <div className="pg-form-row align-items-center justify-content-between mb-4">
            <div className="superset-field-info">
              <label>Rounds</label>
              <p>How many times to repeat the superset</p>
            </div>
            <div className="pg-counter-input">
              <button>−</button>
              <span>3</span>
              <button>+</button>
            </div>
          </div>

          <hr className="modal-hr" />

          <div className="pg-form-row align-items-center justify-content-between mb-4">
            <div className="superset-field-info">
              <label>Rest after pair</label>
              <p>Recovery time after completing one full round</p>
            </div>
            <div className="pg-counter-input">
              <button>−</button>
              <span>10s</span>
              <button>+</button>
            </div>
          </div>

          <div className="pg-rest-pills mb-4">
            {['0s', '30s', '60s', '90s'].map((time) => (
              <button key={time} className={`pg-rest-pill ${time === '30s' ? 'active' : ''}`}>
                {time}
              </button>
            ))}
          </div>

          <div className="pg-modal-actions">
            <button className="pg-modal-cancel-btn" onClick={() => setIsEditSupersetOpen(false)}>
              Cancel
            </button>
            <button className="pg-modal-save-btn m-0" onClick={() => setIsEditSupersetOpen(false)}>
              Save Changes
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={isDraftSavedModalOpen}
        onCancel={() => {
          setIsDraftSavedModalOpen(false);
          showWorkoutSavedToast();
        }}
        footer={null}
        closable={false}
        centered
        width={518}
        rootClassName="logout-modal program-save"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            background: 'rgba(0, 0, 0, 0.40)',
          },
        }}
      >
        <div className="logout-modal-content">
          <div className="logout-icon-wrapper">{confirmationSVG?.draft}</div>
          <h2>Your program has been saved</h2>
          <div className="draft-saved-actions">
            <button
              className="btn-back"
              onClick={() => {
                setIsDraftSavedModalOpen(false);
                showWorkoutSavedToast();
              }}
            >
              {confirmationSVG?.back} Back to Edit
            </button>
            <button
              className="btn-proceed"
              onClick={() => {
                setIsDraftSavedModalOpen(false);
                setIsShareModalOpen(true);
                showWorkoutSavedToast();
              }}
            >
              Share With Client
            </button>
          </div>
        </div>
      </Modal>

      <AssignToClientModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onAssign={(data) => {
          return new Promise<void>((resolve, reject) => {
            const currentUser = sessionStore.getState().currentUser;
            const uid = auth.currentUser?.uid || currentUser?.uid;
            if (!uid || uid.includes('@') || !savedProgramId) {
              toast.error('Program ID or Trainer ID is missing');
              reject(new Error('Program ID or Trainer ID is missing'));
              return;
            }
            assignToClient(
              {
                docPath: `trainer_app_users/${uid}/programme_plan/${savedProgramId}`,
                programId: savedProgramId,
                clientId: data.clientId,
                startDate: data.startDate,
                notify: data.notify,
                message: data.message,
              },
              {
                onSuccess: () => {
                  setIsAssignModalOpen(false);
                  toast.success('Workout assigned successfully');
                  resolve();
                },
                onError: (error) => {
                  reject(error);
                },
              },
            );
          });
        }}
      />
      <ShareProgramModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        programId={savedProgramId || ''}
        programmeName={rawApiResponse?.result?.program?.data?.programmeName || 'Unnamed'}
        isSent={hasSentToClient}
        isSending={isAssigningToClient}
        onSendMiniPTApp={() => {
          if (activeClientId) {
            const currentUser = sessionStore.getState().currentUser;
            const uid = auth.currentUser?.uid || currentUser?.uid;
            if (!uid || uid.includes('@') || !savedProgramId) {
              toast.error('Please save the program before sending.');
              return;
            }
            // Program is already assigned to a client, directly copy to client
            assignToClient(
              {
                docPath: `trainer_app_users/${uid}/programme_plan/${savedProgramId}`,
                programId: savedProgramId,
                clientId: activeClientId,
                startDate: new Date().toISOString().split('T')[0],
                notify: true,
                message: '',
              },
              {
                onSuccess: () => {
                  setHasSentToClient(true);
                  setIsShareModalOpen(false);
                  toast.success('Workout assigned successfully');
                },
              },
            );
          } else {
            setIsAssignModalOpen(true);
            setIsShareModalOpen(false);
          }
        }}
      />
    </>
  );
};
